import React from 'react';
import uniqBy from 'lodash/uniqBy';
import isEmpty from 'lodash/isEmpty';
import { EntitlementIcon, SandboxLozenge, TrialLozenge } from '@css-xp/base';
import { getChargeElement, pluralize } from '@css-xp/ppc-utils';
import { NoData } from '@css-xp/ppc-common';
import { ProductStatusLozenge, Estimate, CustomPanel } from '@css-xp/ppc-purchase-experience';
import { useStore } from '../../../../store';
import { ProductTrialInfo } from './product-trial-info-lozenge';
import { OrderSummaryAppLineItem } from './types';
import { OperationType } from '../../../../types';
import './integrated-apps.scss';

export type IntegratedAppsProps = {
  appLineItems: OrderSummaryAppLineItem[];
  lineItemId: string;
  totalPriceOfApps: number;
  loading: boolean;
  showSandboxStatus?: boolean;
  selectedCurrency?: string;
};

export const IntegratedAppsCustomPanel: React.FC<IntegratedAppsProps> = ({
  appLineItems,
  lineItemId,
  totalPriceOfApps,
  loading,
  showSandboxStatus,
  selectedCurrency = 'USD',
}) => {
  const [{ selectedLinkedEntitlements }] = useStore();
  return (
    <div className="integrated-line-item-apps-container" data-testId="integrated-line-item-apps-container">
      <div className="integrated-apps-container" key={`integrated-apps-${lineItemId}`}>
        <CustomPanel
          className="line-item-custom-panel"
          heading={
            <>
              <div className="line-item-app-container-selected-product-row-container">
                <div className="existing-site-container">
                  <h4 className="line-item-app-container-selected-product-name" data-testid="selected-app-name">
                    Integrated Apps
                  </h4>
                </div>
                <Estimate
                  currency={selectedCurrency}
                  price={totalPriceOfApps ?? 0}
                  loading={loading}
                  className={
                    'integrated-apps-container-line-items-container-line-item-app-container-selected-app-estimated-price'
                  }
                />
              </div>
            </>
          }
        >
          <div className="integrated-apps-container-line-items-container">
            {appLineItems.map((appLineItem: any) => {
              const defaultAppProductStatuses = appLineItem?.productStatuses ?? [];
              const appProductStatuses = selectedLinkedEntitlements?.[appLineItem.id]?.productStatuses ?? []; // For apps use the updated status from selected app from store
              const productStatuses = uniqBy([...defaultAppProductStatuses, ...appProductStatuses], function (status) {
                return status;
              });

              const isInTrial = appLineItem?.inTrial && appLineItem.operationType !== OperationType.NEW;

              return (
                <div
                  className="integrated-apps-container-line-items-container-line-item-app-container"
                  key={`integrated-apps-${appLineItem?.lineItemId}`}
                >
                  <div className="integrated-apps-container-line-items-container-line-item-app-container-icon">
                    <EntitlementIcon
                      logoId={appLineItem?.logoId}
                      productId={appLineItem?.id}
                      className="avatar medium pt-4"
                      isCloud
                    />
                  </div>
                  <CustomPanel
                    className="line-item-custom-panel"
                    heading={
                      <>
                        <div className="integrated-apps-container-line-items-container-line-item-app-container-selected-app-row-container">
                          <div className="integrated-apps-container-line-items-container-line-item-app-container-selected-app-row-container-header-name">
                            <h4 className="integrated-apps-container-line-items-container-line-item-app-container-selected-app-name integrated-app">
                              {appLineItem?.name}
                            </h4>
                            {(!isEmpty(productStatuses) || isInTrial || appLineItem?.isSandbox) && (
                              <div className="integrated-apps-container-line-items-container-line-item-app-container-selected-app-row-container row-header-lozenge">
                                {showSandboxStatus && appLineItem?.isSandbox && (
                                  <div
                                    className="line-item-product-in-trial"
                                    data-testId={`line-item-app-sandbox-${appLineItem?.lineItemId}`}
                                  >
                                    <SandboxLozenge />
                                  </div>
                                )}
                                {productStatuses?.map((status: string) => {
                                  return <ProductStatusLozenge key={`product-status-${status}`} status={status} />;
                                })}
                                {isInTrial && (
                                  <div
                                    className="line-item-product-in-trial"
                                    data-testId={`line-item-product-in-trial-${appLineItem?.lineItemId}`}
                                  >
                                    <TrialLozenge />
                                  </div>
                                )}
                                <ProductTrialInfo trialLength={appLineItem?.trialLengthInDays} isApp />
                              </div>
                            )}
                          </div>
                          <Estimate
                            currency={selectedCurrency}
                            price={appLineItem?.grandTotal ?? 0}
                            loading={loading}
                            className={
                              'integrated-apps-container-line-items-container-line-item-app-container-selected-app-estimated-price'
                            }
                          />
                        </div>
                      </>
                    }
                  >
                    <div className="integrated-apps-container-line-items-container-line-item-app-container-selected-app-row-container">
                      <p className="integrated-apps-container-line-items-container-line-item-app-container-selected-app-pricing-details">
                        List price{' '}
                      </p>
                      <Estimate
                        currency={selectedCurrency}
                        price={appLineItem?.listPrice ?? 0}
                        loading={loading}
                        className={
                          'integrated-apps-container-line-items-container-line-item-app-container-selected-app-sub-title-pricing-currency'
                        }
                      />
                    </div>
                    {!!appLineItem?.discountPercent && (
                      <div className="integrated-apps-container-line-items-container-line-item-app-container-selected-app-row-container">
                        <p className="integrated-apps-container-line-items-container-line-item-app-container-selected-app-pricing-details">{`Partner discount: ${appLineItem?.discountPercent}%`}</p>
                        <Estimate
                          currency={selectedCurrency}
                          price={-(appLineItem?.totalDiscount ?? 0)}
                          loading={loading}
                          className={
                            'integrated-apps-container-line-items-container-line-item-app-container-selected-app-sub-title-pricing-currency'
                          }
                        />
                      </div>
                    )}
                    <div className="integrated-apps-container-line-items-container-line-item-app-container-selected-app-row-container">
                      <h4 className="integrated-apps-container-line-items-container-line-item-app-container-selected-app-estimate-partner-discount">
                        Estimate incl. discounts
                      </h4>
                      <Estimate
                        currency={selectedCurrency}
                        price={appLineItem?.estimateIncludingPartnerDiscount ?? 0}
                        loading={loading}
                        className={
                          'integrated-apps-container-line-items-container-line-item-app-container-selected-app-estimate-partner-discount-currency'
                        }
                      />
                    </div>
                    <div className="integrated-apps-container-line-items-container-line-item-app-container-info">
                      <div className="integrated-apps-container-line-items-container-line-item-app-container-info-row-container">
                        <p className="integrated-apps-container-line-items-container-line-item-app-container-info-row-container-details">
                          Entitlement number: {appLineItem?.entitlementNumber || <NoData />}
                        </p>
                      </div>
                      <div className="integrated-apps-container-line-items-container-line-item-app-container-info-row-container">
                        <p className="integrated-apps-container-line-items-container-line-item-app-container-info-row-container-details">
                          {`Plan: ${appLineItem?.offeringName}`}
                          {appLineItem?.trialLengthInDays
                            ? ` | ${pluralize(Number(appLineItem?.trialLengthInDays), 'Day')} trial`
                            : ''}
                        </p>
                      </div>
                      <div className="integrated-apps-container-line-items-container-line-item-app-container-info-row-container">
                        <p className="integrated-apps-container-line-items-container-line-item-app-container-info-row-container-details">
                          Estimated {getChargeElement({ productId: appLineItem?.id, labelPlural: true })}
                          {': '}
                          {appLineItem?.activeUsers}
                        </p>
                      </div>
                    </div>
                  </CustomPanel>
                </div>
              );
            })}
          </div>
        </CustomPanel>
      </div>
    </div>
  );
};
